import React from "react";
import * as Yup from "yup";
import { FormikValues } from "formik";
import Validation from "shared/form/Validation";
import { OfflinePaymentFieldName } from "./enum";
import { OfflinePaymentPropTypes } from "./types";
import { useCopies } from "shared/hooks/useCopies";
import { useRegDetail } from "shared/context/regDetail";
import { errorToast } from "shared/utils/toastNotification";
import { ApiPaymentRequest } from "shared/types/ApiPaymentRequest";
import { TransactionStatus } from "shared/enums/TransactionStatus";
import { useMakePayment, useUpdateOfflineTransaction } from "shared/dataloader";
import { ApiPurchaseOrderPaymentReferenceRequest } from "shared/types/ApiPurchaseOrderPaymentReferenceRequest";

export function useOfflinePaymentService({
  errorHandler,
  successHandler,
  offlineTransactionUpdateSuccessHandler,
  ...props
}: OfflinePaymentPropTypes) {
  const copies = useCopies();
  const { offlinePaymentNote } = useRegDetail();
  const { mutateAsync: makePayment } = useMakePayment({ hideLoader: false });
  const { mutateAsync: updateOfflineTransaction } = useUpdateOfflineTransaction();

  const initialValues = React.useMemo(
    () => ({
      [OfflinePaymentFieldName.PAYMENT_REFERENCE_ID]: props.offlinePaymentConfig?.paymentReferenceId ?? ""
    }),
    [props.offlinePaymentConfig?.paymentReferenceId]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        [OfflinePaymentFieldName.PAYMENT_REFERENCE_ID]: Validation.textFieldValidation({
          required: false,
          copies
        })
      }),
    [copies]
  );

  const createHandler = React.useCallback(
    async (values: FormikValues) => {
      const reqObj: ApiPaymentRequest = {
        registrationParticipantId: props.registrationParticipantId,
        token: values[OfflinePaymentFieldName.PAYMENT_REFERENCE_ID],
        purchaseOrderId: props.purchaseOrderId,
        amount: props.amount,
        offlinePayment: true
      };
      try {
        const data = await makePayment(reqObj);
        if (data.transaction.status === TransactionStatus.FAILED) {
          errorToast(copies.get("payment.transaction.failed"));
        } else {
          successHandler?.(data);
        }
      } catch (e) {}
    },
    [copies, makePayment, successHandler, props.amount, props.purchaseOrderId, props.registrationParticipantId]
  );

  const updateHandler = React.useCallback(
    async (values: FormikValues) => {
      const reqObj: ApiPurchaseOrderPaymentReferenceRequest = {
        purchaseOrderId: props.purchaseOrderId,
        providerPaymentId: values[OfflinePaymentFieldName.PAYMENT_REFERENCE_ID]
      };
      try {
        const data = await updateOfflineTransaction(reqObj);
        if (data.transaction?.status === TransactionStatus.FAILED) {
          errorToast(copies.get("payment.transaction.failed"));
        } else {
          offlineTransactionUpdateSuccessHandler?.(data);
        }
      } catch (e) {
        errorHandler?.(e);
      }
    },
    [copies, errorHandler, updateOfflineTransaction, offlineTransactionUpdateSuccessHandler, props.purchaseOrderId]
  );

  const submitHandler = React.useCallback(
    (values: FormikValues) => {
      props.offlinePaymentConfig?.offlinePaymentRequested ? updateHandler(values) : createHandler(values);
    },
    [createHandler, updateHandler, props.offlinePaymentConfig?.offlinePaymentRequested]
  );

  return { initialValues, offlinePaymentNote, validationSchema, submitHandler };
}
